import { useEffect, useRef } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';

import messages from '@/shared/i18n/en/shell';
import { tr } from '@/shared/i18n/translate';
import { getAccessToken } from '@/shared/stores/auth.store';
import { API_BASE } from '@/shared/api/client';
import { scheduleChatListRefresh } from '@/shared/api/chat-list-cache';

import {
  applyChatMessageChanged,
  applyChatMessageCreated,
  applyDmMessageChanged,
  applyDmMessageCreated,
  applyMyChatsChanged,
} from './chat-realtime';
import { emitLiveNotification } from './live-notification-bus';

/**
 * Wire-format envelope from the API SSE channel.  Keep loose — server may add
 * new topics without a client update.  Unknown topics are logged and skipped.
 */
export interface RealtimeEnvelope {
  type: string;
  payload: Record<string, unknown>;
  ts: number;
}

/**
 * Subscribe to `/api/me/stream` for the lifetime of the React tree.
 *
 * Why a custom fetch reader (not EventSource):
 *   - Our auth is JWT in `Authorization: Bearer …`; browser EventSource cannot
 *     attach custom headers.  Cookie auth would be the alternative, but that
 *     would diverge this single endpoint from every other REST call.
 *   - `fetch` + `ReadableStream` lets us reuse the same Bearer header surface,
 *     parse SSE frames manually, and reconnect with backoff.
 *
 * Recipes:
 *   - On each `task.*` event (incl. `task.due_changed`, a Deadline move) we
 *     invalidate the canonical task entity + lists — boards, calendars, my My
 *     Work lists and private panels — and every Team Overview.
 *   - On `milestone.changed` the project's milestones, the team timeline and
 *     Team Overview; on `project.visibility_changed` (team ⇄ restricted) the
 *     same as an access change plus the project's own queries.
 *   - On `me.plan.changed` / `me.mine.changed` (my private plan, other device
 *     or tab) we invalidate `['me','work']` + `['me','home']` / `['me','mine',taskId]`.
 *   - On `notification.created` we invalidate the inbox + `['me','unread-summary']`;
 *     `chat.message_*` / `dm.message_*` patch the Chat list in place
 *     (`chat-realtime.ts`) instead of refetching it per message.
 *   - On `team.*` / `project.*` we invalidate broader queries.
 *   - Unknown topics are logged but otherwise ignored — adding a new event
 *     server-side doesn't break older clients.
 */
export function useRealtimeStream(opts: { enabled: boolean }): void {
  const queryClient = useQueryClient();
  const enabledRef = useRef(opts.enabled);
  enabledRef.current = opts.enabled;

  useEffect(() => {
    if (!opts.enabled) return;

    const controller = new AbortController();
    let stopped = false;
    let attempt = 0;

    const run = async () => {
      while (!stopped && enabledRef.current) {
        try {
          await openStream(queryClient, controller.signal);
          attempt = 0; // clean disconnect → reset backoff
        } catch (err) {
          if (controller.signal.aborted) return;
          attempt += 1;
          const delay = Math.min(30_000, 1000 * 2 ** Math.min(attempt, 5));

          console.warn(`[realtime] disconnected, retry in ${delay}ms`, err);
          await wait(delay, controller.signal);
        }
      }
    };

    void run();

    return () => {
      stopped = true;
      controller.abort();
    };
  }, [opts.enabled, queryClient]);
}

async function openStream(queryClient: QueryClient, signal: AbortSignal): Promise<void> {
  const token = getAccessToken();
  if (!token) {
    // No token → fail fast so the outer loop retries after the auth store rehydrates.
    throw new Error('no-access-token');
  }
  const response = await fetch(`${API_BASE}/api/me/stream`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'text/event-stream',
    },
    signal,
  });
  if (!response.ok || !response.body) {
    throw new Error(`stream-open-failed: HTTP ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });

    // SSE frames are separated by a blank line.
    let idx: number;
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const lines = frame.split('\n');
      const eventType = lines.find((line) => line.startsWith('event:'))?.slice(6).trim();
      // Nest ends a rejected/revoked stream with a plain-text transport error.
      // It is not a domain envelope; EOF retains the existing reconnect path.
      if (eventType === 'error') continue;
      const dataLine = lines.find((line) => line.startsWith('data:'));
      if (!dataLine) continue;
      const raw = dataLine.slice(5).trim();
      if (!raw) continue;
      try {
        const envelope = JSON.parse(raw) as RealtimeEnvelope;
        if (envelope.type === 'heartbeat') continue;
        applyEnvelope(queryClient, envelope);
      } catch (err) {
        console.warn('[realtime] malformed SSE frame', { raw, err });
      }
    }
  }
}

/**
 * Map a server envelope onto TanStack Query invalidations.
 *
 * Conservative defaults: when in doubt invalidate broadly.  An extra empty
 * refetch is cheap; a stale UI is a real UX bug.  Exported for tests.
 */
export function applyEnvelope(qc: QueryClient, envelope: RealtimeEnvelope): void {
  const { type, payload } = envelope;
  const taskId = payload.taskId as string | undefined;
  const projectId = payload.projectId as string | undefined;

  switch (type) {
    // Source-neutral signal used when an ACL change or restricted event must
    // invalidate cached views without disclosing the source id or event kind.
    case 'scope.invalidated':
      void qc.invalidateQueries();
      break;

    // ── task.* ────────────────────────────────────────────────────────────
    case 'task.created':
    case 'task.updated':
    case 'task.assigned':
    case 'task.respondent_changed':
    case 'task.collaborators_changed':
    case 'task.status_changed':
    case 'task.due_changed':
    case 'task.archived':
    case 'task.restored':
    case 'task.tag_attached':
    case 'task.tag_detached':
    case 'task.watchers_changed':
    case 'task.pinned':
    case 'task.unpinned':
      if (taskId) {
        void qc.invalidateQueries({ queryKey: ['tasks', taskId] });
        void qc.invalidateQueries({ queryKey: ['tasks', taskId, 'history'] });
        void qc.invalidateQueries({ queryKey: ['tasks', taskId, 'relations'] });
        void qc.invalidateQueries({ queryKey: ['tasks', taskId, 'children'] });
      }
      // List surfaces — invalidate everything that could include this task.
      void qc.invalidateQueries({ queryKey: ['me', 'tasks'] });
      void qc.invalidateQueries({ queryKey: ['me', 'recent-tasks'] });
      if (projectId) {
        void qc.invalidateQueries({ queryKey: ['projects', projectId, 'tasks'] });
      }
      invalidateMyWork(qc);
      invalidateTeamOverviews(qc);
      break;
    case 'task.deleted_permanently':
      if (taskId) qc.removeQueries({ queryKey: ['tasks', taskId] });
      void qc.invalidateQueries({ queryKey: ['me', 'tasks'] });
      if (projectId) void qc.invalidateQueries({ queryKey: ['projects', projectId, 'tasks'] });
      invalidateMyWork(qc);
      invalidateTeamOverviews(qc);
      break;
    case 'task.commented':
    case 'task.comment_updated':
    case 'task.comment_deleted':
      if (taskId) {
        void qc.invalidateQueries({ queryKey: ['tasks', taskId, 'comments'] });
        void qc.invalidateQueries({ queryKey: ['tasks', taskId] });
      }
      break;

    // ── project.* ─────────────────────────────────────────────────────────
    case 'project.created':
    case 'project.archived':
    case 'project.unarchived':
    case 'project.moved_to_team':
      void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'teams' });
      void qc.invalidateQueries({ queryKey: ['me', 'projects'] });
      // A project's channel appears / moves to Archived in its team's forum.
      void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'chat' && q.queryKey[2] === 'topics' });
      scheduleChatListRefresh(qc);
      break;

    case 'project.access_granted':
    case 'project.access_revoked':
      invalidateProjectAccess(qc);
      break;
    case 'project.visibility_changed':
      // Team ⇄ restricted: the open project page, its tasks / files / milestones and the
      // project's chat channel may have just become (in)visible to me.
      if (projectId) void qc.invalidateQueries({ queryKey: ['projects', projectId] });
      void qc.invalidateQueries({
        predicate: (q) => q.queryKey[0] === 'chat' && q.queryKey[2] === 'topics',
      });
      invalidateProjectAccess(qc);
      break;

    // ── milestone.* ───────────────────────────────────────────────────────
    case 'milestone.changed':
      if (projectId) void qc.invalidateQueries({ queryKey: ['projects', projectId, 'milestones'] });
      // Team timeline (`['teams', slug, 'milestones']`) — the envelope has no slug.
      void qc.invalidateQueries({
        predicate: (q) => q.queryKey[0] === 'teams' && q.queryKey[2] === 'milestones',
      });
      invalidateTeamOverviews(qc);
      break;

    // ── team.* ────────────────────────────────────────────────────────────
    case 'team.member_added':
    case 'team.member_removed':
    case 'team.role_changed':
      void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'teams' });
      scheduleChatListRefresh(qc);
      void qc.invalidateQueries({ queryKey: ['me', 'projects'] });
      // Losing (or gaining) access changes what My Work may show (G6).
      invalidateMyWork(qc);
      break;

    // ── me.* (my private plan — sent only to me, never carries a teamId) ──
    case 'me.plan.changed': {
      invalidateMyWorkLists(qc);
      const taskIds = Array.isArray(payload.taskIds) ? payload.taskIds : [];
      for (const id of taskIds) {
        if (typeof id === 'string') void qc.invalidateQueries({ queryKey: ['me', 'mine', id] });
      }
      break;
    }
    case 'me.mine.changed':
      if (taskId) void qc.invalidateQueries({ queryKey: ['me', 'mine', taskId] });
      break;

    // ── notification.created ──────────────────────────────────────────────
    case 'notification.created':
      void qc.invalidateQueries({ queryKey: ['me', 'inbox'] });
      void qc.invalidateQueries({ queryKey: ['me', 'inbox', 'unread'] });
      void qc.invalidateQueries({ queryKey: ['me', 'unread-summary'] });
      // Hand the presented copy to the toast layer for a live, dismissible
      // in-app notification (gated to important kinds there). The server only
      // fires this SSE frame when the recipient's inbox transport is on, so
      // muted users never see a toast.
      emitLiveNotification({
        id: (payload.notificationId as string | null) ?? null,
        kind: (payload.kind as string | undefined) ?? 'unknown',
        title: (payload.title as string | undefined) ?? tr(messages, 'newNotification'),
        body: (payload.body as string | undefined) ?? '',
        url: (payload.url as string | undefined) ?? '/inbox',
        important: (payload.important as boolean | undefined) ?? false,
      });
      break;

    // ── time.* (user-private) ─────────────────────────────────────────────
    case 'time.started':
    case 'time.stopped':
    case 'time.logged':
    case 'time.removed':
      void qc.invalidateQueries({ queryKey: ['me', 'time', 'active'] });
      if (taskId) {
        void qc.invalidateQueries({ queryKey: ['tasks', taskId, 'time'] });
        void qc.invalidateQueries({ queryKey: ['tasks', taskId] });
      }
      break;

    // ── chat.* (team-scoped) / dm.* (1:1) — patched in place, see chat-realtime.ts ──
    case 'chat.message_created':
      applyChatMessageCreated(qc, payload);
      break;
    case 'chat.message_edited':
    case 'chat.message_deleted':
    case 'chat.message_reacted':
      applyChatMessageChanged(qc, type, payload);
      break;
    case 'chat.topic_created':
      void qc.invalidateQueries({
        predicate: (q) => q.queryKey[0] === 'chat' && q.queryKey[2] === 'topics',
      });
      break;
    case 'dm.message_created':
      applyDmMessageCreated(qc, payload);
      break;
    case 'dm.message_edited':
    case 'dm.message_deleted':
      applyDmMessageChanged(qc, payload);
      break;
    // My pin / mute / mark-read on another tab or device (only ever sent to me).
    case 'me.chats.changed':
      applyMyChatsChanged(qc);
      break;

    // ── folder.* (project file folders, migration 0053) ───────────────────
    // All folder events carry `projectId` in their payload; invalidating the
    // per-project folders + files queries refreshes both the tree on the left
    // and the file grid on the right.  Access granted / revoked also refresh
    // the per-folder access list when the right-panel ACL tab is open.
    case 'folder.created':
    case 'folder.renamed':
    case 'folder.moved':
    case 'folder.visibility_changed':
    case 'folder.deleted': {
      const projectId = payload.projectId as string | undefined;
      if (projectId) {
        void qc.invalidateQueries({ queryKey: ['projects', projectId, 'folders'] });
        void qc.invalidateQueries({ queryKey: ['projects', projectId, 'files'] });
      }
      break;
    }
    case 'folder.access_granted':
    case 'folder.access_revoked': {
      const projectId = payload.projectId as string | undefined;
      const folderId = payload.folderId as string | undefined;
      if (projectId) {
        // Visibility may have flipped for the recipient — refresh both lists.
        void qc.invalidateQueries({ queryKey: ['projects', projectId, 'folders'] });
        void qc.invalidateQueries({ queryKey: ['projects', projectId, 'files'] });
      }
      if (folderId) {
        void qc.invalidateQueries({ queryKey: ['folders', folderId, 'access'] });
      }
      break;
    }

    // ── file.moved (folder reassignment, migration 0053) ──────────────────
    case 'file.moved': {
      const projectId = payload.projectId as string | undefined;
      if (projectId) {
        void qc.invalidateQueries({ queryKey: ['projects', projectId, 'files'] });
      }
      break;
    }

    default:
      // eslint-disable-next-line no-console
      console.debug('[realtime] unhandled envelope', envelope);
  }
}

/** Who may see a project changed (a grant / revoke, or a visibility flip). */
function invalidateProjectAccess(qc: QueryClient): void {
  // A project channel may have (dis)appeared from a team's forum and its row.
  scheduleChatListRefresh(qc);
  // Broad team invalidation covers project lists + members tab.
  void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'teams' });
  // Picker queries (eligible-people) live under teams/.../projects/.../eligible-people
  // — already covered by the predicate above, but be explicit so a future query-key
  // refactor doesn't silently lose this.  Also invalidate `me/tasks` because some
  // tasks may now become visible (or invisible) when access changes.
  void qc.invalidateQueries({ predicate: (q) => q.queryKey.includes('eligible-people') });
  void qc.invalidateQueries({ queryKey: ['me', 'tasks'] });
  void qc.invalidateQueries({ queryKey: ['me', 'projects'] });
  invalidateMyWork(qc);
}

/**
 * Team Overview (`teamOverviewKeys` in `shared/api/team-overview.ts`:
 * `['teams', slug, 'overview', day]`) of every team — the envelope carries no
 * slug, and only the overview on screen actually refetches.
 */
function invalidateTeamOverviews(qc: QueryClient): void {
  void qc.invalidateQueries({
    predicate: (q) => q.queryKey[0] === 'teams' && q.queryKey[2] === 'overview',
  });
}

/**
 * My Work rows mirror team tasks (status, assignee, access), so team task
 * events refresh my own My Work lists and private panels — locally, on my
 * device only. Keys mirror `myWorkKeys` in `shared/api/my-work.ts` (not
 * imported, so the always-loaded shell stays free of My Work code).
 */
function invalidateMyWork(qc: QueryClient): void {
  invalidateMyWorkLists(qc);
  void qc.invalidateQueries({ queryKey: ['me', 'mine'] });
}

/**
 * While my own plan writes are in flight (optimistic rows on screen), skip:
 * a refetch now would briefly undo the queued moves, and the last write
 * refetches on settle anyway. Home's Morning card + My week are built from the
 * same plan and tasks (`['me','home']`, mirrors `meHomeKeys.homeAll`).
 */
function invalidateMyWorkLists(qc: QueryClient): void {
  if (qc.isMutating({ mutationKey: ['me', 'work', 'mutation', 'plan'] }) > 0) return;
  void qc.invalidateQueries({ queryKey: ['me', 'work'] });
  void qc.invalidateQueries({ queryKey: ['me', 'home'] });
}

function wait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const finish = () => {
      clearTimeout(t);
      signal.removeEventListener('abort', finish);
      resolve();
    };
    const t = setTimeout(finish, ms);
    if (signal.aborted) finish();
    else signal.addEventListener('abort', finish, { once: true });
  });
}
